import type { CurrentUserOrganization } from "@multi-chat/shared";

/**
 * Which organizations a Platform Admin sees directly in the switcher.
 * Device-local UI preference only: it never grants, limits, or revokes access.
 * Every listed organization is still authorized by the server on each request.
 */
export interface PlatformAdminOrganizationList {
  /** Entered or added from the directory, in the order they were added. */
  added: readonly number[];
  /** The Platform Admin's own memberships removed from the switcher. */
  hidden: readonly number[];
}

export const EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST: PlatformAdminOrganizationList = {
  added: [],
  hidden: [],
};

const STORAGE_PREFIX = "multi-chat:platform-admin-organizations:v1:";

function storageKey(userId: number): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function parseIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const ids: number[] = [];
  for (const item of value) {
    if (typeof item === "number" && Number.isInteger(item) && item > 0 && !ids.includes(item)) {
      ids.push(item);
    }
  }
  return ids;
}

/**
 * Before this list existed, only the selected organization was persisted.
 * Seed from it so an organization a Platform Admin already entered stays listed.
 */
export function readPlatformAdminOrganizationList(
  userId: number,
  legacySelectedId: number | null,
): PlatformAdminOrganizationList {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw === null) {
      return { added: legacySelectedId === null ? [] : [legacySelectedId], hidden: [] };
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST;
    const record = parsed as { added?: unknown; hidden?: unknown };
    return { added: parseIds(record.added), hidden: parseIds(record.hidden) };
  } catch {
    return EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST;
  }
}

export function writePlatformAdminOrganizationList(
  userId: number,
  list: PlatformAdminOrganizationList,
): void {
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify({ added: list.added, hidden: list.hidden }),
    );
  } catch {
    // Storage can be unavailable (private mode, quota); the list is a convenience.
  }
}

export function addToPlatformAdminOrganizationList(
  list: PlatformAdminOrganizationList,
  organizationId: number,
): PlatformAdminOrganizationList {
  return {
    added: list.added.includes(organizationId) ? list.added : [...list.added, organizationId],
    hidden: list.hidden.filter((id) => id !== organizationId),
  };
}

export function removeFromPlatformAdminOrganizationList(
  list: PlatformAdminOrganizationList,
  organizationId: number,
): PlatformAdminOrganizationList {
  return {
    added: list.added.filter((id) => id !== organizationId),
    hidden: list.hidden.includes(organizationId) ? list.hidden : [...list.hidden, organizationId],
  };
}

/**
 * Switcher organizations for a Platform Admin: own memberships that are not
 * hidden, then added organizations the server has already authorized.
 */
export function visiblePlatformAdminOrganizations(input: {
  memberships: readonly CurrentUserOrganization[];
  list: PlatformAdminOrganizationList;
  authorized: ReadonlyMap<number, CurrentUserOrganization>;
}): CurrentUserOrganization[] {
  const hidden = new Set(input.list.hidden);
  const membershipIds = new Set(input.memberships.map((organization) => organization.id));
  const visibleMemberships = input.memberships.filter((organization) => !hidden.has(organization.id));
  const added = input.list.added.flatMap((id) => {
    if (membershipIds.has(id)) return [];
    const organization = input.authorized.get(id);
    return organization ? [organization] : [];
  });
  return [...visibleMemberships, ...added];
}

/** Organization to select after removing the selected one, or null for the chooser. */
export function selectionAfterRemoval(
  organizations: readonly Pick<CurrentUserOrganization, "id">[],
  removedId: number,
): number | null {
  return organizations.find((organization) => organization.id !== removedId)?.id ?? null;
}
